import {
  AloftArea,
  AloftPrice,
  CraftsmanCostProps,
  ORDER_FEATURES,
  getCraftsmanCost,
  isDefined,
  isUndefined,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { selectCompSequence } from '../orderSelectors.ts';
import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectPropertyIdentificationEntities } from '../propertyDataSelectors/propertyIdentificationSelectors.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectCostProps } from './selectCostProps.ts';

const calculatePatioCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  id: string,
) => {
  const improvementsForTransaction = improvements[id];
  const identification = propertyIdentification[id];

  if (!improvementsForTransaction || !identification) return undefined;

  const { patio, craftsmanQuality, propertyType } = improvementsForTransaction;
  const zipCode = identification.address.PostalCode;

  if (isUndefined(craftsmanQuality) || isUndefined(zipCode) || !patio || !propertyType) {
    return undefined;
  }

  const params = patio.area
    ? {
        hasFeature: ORDER_FEATURES.patio.hasFeature(improvementsForTransaction),
        propertyType,
        type: patio.type,
        quality: patio.quality ?? craftsmanQuality,
        sqft: AloftArea.fromNumber(patio.area.unitAmount, patio.area.unit),
        zipCode,
      }
    : {
        hasFeature: ORDER_FEATURES.patio.hasFeature(improvementsForTransaction),
        propertyType,
        type: patio.type,
        quality: patio.quality ?? craftsmanQuality,
        zipCode,
      };

  const calculator = getCraftsmanCost(costProps);
  return calculator.getPatioCost(params);
};

const getTotalOrBreakdownPatioCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  id: string,
  type: 'totalCost' | 'zipOnly',
) => {
  const costOutput = calculatePatioCost(costProps, improvements, propertyIdentification, id);

  if (!costOutput) return AloftPrice.zero;
  return type === 'totalCost' ? costOutput.totalCost : costOutput.multiplierBreakdown[type];
};

const getTotalPatioCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  id: string,
) => getTotalOrBreakdownPatioCost(costProps, improvements, propertyIdentification, id, 'totalCost');

export const selectPatioCost = createPriceSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state, id: string) => id,
  ],
  getTotalPatioCost,
);

export const selectZipOnlyPatioCost = createPriceSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state, id: string) => id,
  ],
  (costProps, improvements, propertyIdentification, id) =>
    getTotalOrBreakdownPatioCost(costProps, improvements, propertyIdentification, id, 'zipOnly'),
);

export const selectPatioCostDisplay = createSelector([selectPatioCost], (patioCost) =>
  patioCost?.toString(true),
);

export const selectPatioCosts = createSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    selectCompSequence,
  ],
  (costProps, improvements, propertyIdentification, compSequence) =>
    ['subject', ...compSequence].reduce((acc: Record<string, AloftPrice>, propID) => {
      const cost = getTotalPatioCost(costProps, improvements, propertyIdentification, propID);
      if (isDefined(cost)) acc[propID] = cost;
      return acc;
    }, {}),
);
